import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Branding() {
  const { currentAccount, reloadContext } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/account/branding").then((r) => setForm(r.data)).catch((e) => toast.error(apiError(e))); }, [currentAccount]);
  if (!form) return <PageLoader />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try { await api.patch("/account/branding", form); await reloadContext(); toast.success("Branding saved"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Branding" description="Configure how your workspace appears. Full white-labeling expands in later phases." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4">
          <div><Label className="text-xs font-semibold text-slate-700">Company display name</Label>
            <Input value={form.company_display_name || ""} onChange={set("company_display_name")} className="mt-1.5" data-testid="brand-name" /></div>
          <div><Label className="text-xs font-semibold text-slate-700">Login tagline</Label>
            <Input value={form.login_tagline || ""} onChange={set("login_tagline")} className="mt-1.5" data-testid="brand-tagline" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs font-semibold text-slate-700">Logo URL</Label>
              <Input value={form.logo_url || ""} onChange={set("logo_url")} className="mt-1.5" placeholder="https://…" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Favicon URL</Label>
              <Input value={form.favicon_url || ""} onChange={set("favicon_url")} className="mt-1.5" placeholder="https://…" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-xs font-semibold text-slate-700">Primary color</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="color" value={form.primary_color || "#006090"} onChange={set("primary_color")} className="h-9 w-12 rounded border border-slate-200" data-testid="brand-primary" />
                <Input value={form.primary_color || ""} onChange={set("primary_color")} className="font-mono" /></div></div>
            <div><Label className="text-xs font-semibold text-slate-700">Accent color</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input type="color" value={form.accent_color || "#0093D6"} onChange={set("accent_color")} className="h-9 w-12 rounded border border-slate-200" data-testid="brand-accent" />
                <Input value={form.accent_color || ""} onChange={set("accent_color")} className="font-mono" /></div></div>
          </div>
          <Button onClick={save} disabled={saving} data-testid="save-branding" className="bg-[#006090] hover:bg-[#004E75]">Save branding</Button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Preview</p>
          <div className="rounded-lg overflow-hidden border border-slate-200">
            <div className="h-16 flex items-center gap-2 px-4" style={{ background: form.primary_color || "#006090" }}>
              <div className="w-8 h-8 rounded flex items-center justify-center text-white font-bold" style={{ background: form.accent_color || "#0093D6" }}>
                {(form.company_display_name || "T")[0]}
              </div>
              <span className="text-white font-semibold font-display">{form.company_display_name || "Top-Recruit"}</span>
            </div>
            <div className="p-4"><p className="text-sm text-slate-500">{form.login_tagline || "Your recruiting workspace"}</p>
              <button className="mt-3 rounded-md px-3 py-1.5 text-sm text-white" style={{ background: form.accent_color || "#0093D6" }}>Accent button</button></div>
          </div>
        </div>
      </div>
    </div>
  );
}
